import React from 'react';
import { Box, ButtonBase, IconButton, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import dayjs, { Dayjs } from 'dayjs';
import { ISO_DAY, startOfWeek, type WeekStart } from '../utils/dateRange';

/** A range being picked: the end is null until the second click. */
export interface RangeSelection {
  start: string | null;
  end: string | null;
}

export interface RangeCalendarProps {
  value: RangeSelection;
  onChange: (next: RangeSelection) => void;
  /** First day of the leftmost month shown, YYYY-MM-DD. Uncontrolled if omitted. */
  month?: string;
  onMonthChange?: (month: string) => void;
  /** Months shown side by side. */
  months?: number;
  minDate?: string;
  maxDate?: string;
  weekStartsOn?: WeekStart;
  /** Marked with a ring. Defaults to the real today. */
  today?: string;
}

type View = { kind: 'days' } | { kind: 'months'; panel: number; year: number };

const KEY_STEPS: Record<string, [number, 'day' | 'month']> = {
  ArrowLeft: [-1, 'day'],
  ArrowRight: [1, 'day'],
  ArrowUp: [-7, 'day'],
  ArrowDown: [7, 'day'],
  PageUp: [-1, 'month'],
  PageDown: [1, 'month'],
};

const cellSize = (t: Theme) => t.ds.sizes.control.md;

/**
 * Inline calendar for picking a date range: one or more months side by side.
 *
 * Click a start day, then an end day; picking the end before the start swaps
 * them. While only the start is chosen, hovering previews the range. Click a
 * month's title to jump by month and year instead of paging.
 *
 * Keyboard: arrows move a day or a week, Page Up / Page Down a month, Enter
 * or Space picks.
 *
 * Usually used through `DateRangePicker`, which adds presets and a trigger.
 * Use it directly only where the calendar should sit on the page itself.
 */
export function RangeCalendar({
  value,
  onChange,
  month: monthProp,
  onMonthChange,
  months = 2,
  minDate,
  maxDate,
  weekStartsOn = 0,
  today,
}: RangeCalendarProps) {
  const todayIso = (today ? dayjs(today) : dayjs()).format(ISO_DAY);
  const [innerMonth, setInnerMonth] = React.useState(
    () => monthProp ?? dayjs(value.end ?? value.start ?? todayIso).startOf('month').subtract(months - 1, 'month').format(ISO_DAY),
  );
  const month = dayjs(monthProp ?? innerMonth).startOf('month');
  const setMonth = (next: Dayjs) => {
    const iso = next.startOf('month').format(ISO_DAY);
    setInnerMonth(iso);
    onMonthChange?.(iso);
  };

  const [view, setView] = React.useState<View>({ kind: 'days' });
  const [hover, setHover] = React.useState<string | null>(null);
  const [focusDay, setFocusDay] = React.useState<string>(value.end ?? value.start ?? todayIso);
  const moveFocus = React.useRef(false);
  const gridRef = React.useRef<HTMLDivElement | null>(null);

  const lastVisible = month.add(months - 1, 'month').endOf('month');
  const isVisible = (iso: string) => !dayjs(iso).isBefore(month, 'day') && !dayjs(iso).isAfter(lastVisible, 'day');
  const isDisabled = (iso: string) => (!!minDate && iso < minDate) || (!!maxDate && iso > maxDate);

  // Only one day is in the tab order. If the remembered one has paged out of
  // view, the first of the first month stands in so the grid stays reachable.
  const tabbableDay = isVisible(focusDay) ? focusDay : month.format(ISO_DAY);

  React.useEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    gridRef.current?.querySelector<HTMLElement>(`[data-day="${focusDay}"]`)?.focus();
  }, [focusDay, monthProp, innerMonth]);

  const pick = (iso: string) => {
    if (!value.start || value.end) {
      onChange({ start: iso, end: null });
    } else if (iso < value.start) {
      onChange({ start: iso, end: value.start });
    } else {
      onChange({ start: value.start, end: iso });
    }
    setHover(null);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const step = KEY_STEPS[e.key];
    if (!step || view.kind !== 'days') return;
    e.preventDefault();
    let next = dayjs(tabbableDay).add(step[0], step[1]);
    if (minDate && next.isBefore(minDate, 'day')) next = dayjs(minDate);
    if (maxDate && next.isAfter(maxDate, 'day')) next = dayjs(maxDate);
    if (next.isBefore(month, 'day')) setMonth(next);
    else if (next.isAfter(lastVisible, 'day')) setMonth(next.startOf('month').subtract(months - 1, 'month'));
    moveFocus.current = true;
    setFocusDay(next.format(ISO_DAY));
  };

  // The span to shade: the chosen range, or a preview while the end is open.
  const previewing = !!value.start && !value.end && !!hover;
  const other = value.end ?? (previewing ? hover : null);
  const lo = value.start && other ? (value.start < other ? value.start : other) : value.start;
  const hi = value.start && other ? (value.start < other ? other : value.start) : value.start;

  const canGoBack = !minDate || month.isAfter(dayjs(minDate), 'month');
  const canGoForward = !maxDate || lastVisible.isBefore(dayjs(maxDate), 'month');
  const navSpacer = <Box sx={(t) => ({ width: t.ds.sizes.control.sm, flexShrink: 0 })} />;

  const weekdayNames = Array.from({ length: 7 }, (_, i) =>
    startOfWeek(dayjs(), weekStartsOn).add(i, 'day').format('dd'),
  );

  const panelWidth = (t: Theme) => cellSize(t) * 7;

  if (view.kind === 'months') {
    const minMonth = minDate ? dayjs(minDate).startOf('month') : null;
    const maxMonth = maxDate ? dayjs(maxDate).startOf('month') : null;
    return (
      <Box
        sx={(t) => ({ width: panelWidth(t) * months + (months - 1) * 24, maxWidth: '100%' })}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <IconButton
            size="small"
            aria-label="Previous year"
            disabled={!!minMonth && view.year <= minMonth.year()}
            onClick={() => setView({ ...view, year: view.year - 1 })}
          >
            <ChevronLeftRounded fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2">{view.year}</Typography>
          <IconButton
            size="small"
            aria-label="Next year"
            disabled={!!maxMonth && view.year >= maxMonth.year()}
            onClick={() => setView({ ...view, year: view.year + 1 })}
          >
            <ChevronRightRounded fontSize="small" />
          </IconButton>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          {Array.from({ length: 12 }, (_, m) => {
            const candidate = dayjs(new Date(view.year, m, 1));
            const out =
              (!!minMonth && candidate.isBefore(minMonth, 'month')) ||
              (!!maxMonth && candidate.isAfter(maxMonth, 'month'));
            const shown = candidate.isSame(month.add(view.panel, 'month'), 'month');
            return (
              <ButtonBase
                key={m}
                disabled={out}
                onClick={() => {
                  setMonth(candidate.subtract(view.panel, 'month'));
                  setView({ kind: 'days' });
                }}
                sx={(t) => ({
                  ...t.typography.body2,
                  height: t.ds.sizes.control.lg,
                  borderRadius: `${t.ds.radius.sm}px`,
                  color: t.tokens.text.primary,
                  '&:hover': { bgcolor: t.palette.action.hover },
                  '&.Mui-focusVisible': { outline: `2px solid ${t.tokens.border.focus}` },
                  '&.Mui-disabled': { color: t.tokens.text.disabled },
                  ...(shown && {
                    bgcolor: t.tokens.brand.primarySoft,
                    color: t.tokens.text.accent,
                    fontWeight: 600,
                  }),
                })}
              >
                {candidate.format('MMM')}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Stack
      ref={gridRef}
      direction="row"
      spacing={3}
      onKeyDown={onKeyDown}
      onMouseLeave={() => setHover(null)}
    >
      {Array.from({ length: months }, (_, panel) => {
        const panelMonth = month.add(panel, 'month');
        const gridStart = startOfWeek(panelMonth, weekStartsOn);
        // Always six rows, so the popover does not change height as you page.
        const days = Array.from({ length: 42 }, (_, i) => gridStart.add(i, 'day'));

        return (
          <Box key={panel} role="grid" aria-label={panelMonth.format('MMMM YYYY')} sx={(t) => ({ width: panelWidth(t) })}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              {panel === 0 ? (
                <IconButton
                  size="small"
                  aria-label="Previous month"
                  disabled={!canGoBack}
                  onClick={() => setMonth(month.subtract(1, 'month'))}
                >
                  <ChevronLeftRounded fontSize="small" />
                </IconButton>
              ) : navSpacer}
              <ButtonBase
                onClick={() => setView({ kind: 'months', panel, year: panelMonth.year() })}
                aria-label={`${panelMonth.format('MMMM YYYY')}, choose another month`}
                sx={(t) => ({
                  ...t.typography.subtitle2,
                  px: 1,
                  height: t.ds.sizes.control.sm,
                  borderRadius: `${t.ds.radius.sm}px`,
                  color: t.tokens.text.primary,
                  '&:hover': { bgcolor: t.palette.action.hover },
                  '&.Mui-focusVisible': { outline: `2px solid ${t.tokens.border.focus}` },
                })}
              >
                {panelMonth.format('MMMM YYYY')}
              </ButtonBase>
              {panel === months - 1 ? (
                <IconButton
                  size="small"
                  aria-label="Next month"
                  disabled={!canGoForward}
                  onClick={() => setMonth(month.add(1, 'month'))}
                >
                  <ChevronRightRounded fontSize="small" />
                </IconButton>
              ) : navSpacer}
            </Stack>

            <Box role="row" sx={(t) => ({ display: 'grid', gridTemplateColumns: `repeat(7, ${cellSize(t)}px)` })}>
              {weekdayNames.map((name) => (
                <Typography
                  key={name}
                  role="columnheader"
                  variant="caption"
                  sx={(t) => ({ textAlign: 'center', color: t.tokens.text.tertiary, lineHeight: `${t.ds.sizes.control.sm}px` })}
                >
                  {name}
                </Typography>
              ))}
            </Box>

            <Box sx={(t) => ({ display: 'grid', gridTemplateColumns: `repeat(7, ${cellSize(t)}px)`, gridAutoRows: cellSize(t) })}>
              {days.map((day, index) => {
                const iso = day.format(ISO_DAY);
                if (!day.isSame(panelMonth, 'month')) return <Box key={iso} role="gridcell" />;

                const disabled = isDisabled(iso);
                const endpoint = iso === lo || iso === hi;
                const spans = !!lo && !!hi && lo !== hi;
                const inside = spans && iso > (lo as string) && iso < (hi as string);
                const column = index % 7;
                const band = (t: Theme) => (previewing ? t.tokens.brand.primarySofter : t.tokens.brand.primarySoft);

                return (
                  <Box
                    key={iso}
                    role="gridcell"
                    aria-selected={endpoint || inside}
                    sx={(t) => ({
                      // The shading sits on the cell, not the button, so the
                      // range reads as one continuous band across the week.
                      background: inside
                        ? band(t)
                        : spans && iso === lo
                          ? `linear-gradient(to right, transparent 50%, ${band(t)} 50%)`
                          : spans && iso === hi
                            ? `linear-gradient(to left, transparent 50%, ${band(t)} 50%)`
                            : 'transparent',
                      ...(inside && (column === 0 || day.date() === 1) && {
                        borderTopLeftRadius: `${t.ds.radius.sm}px`,
                        borderBottomLeftRadius: `${t.ds.radius.sm}px`,
                      }),
                      ...(inside && (column === 6 || day.date() === day.daysInMonth()) && {
                        borderTopRightRadius: `${t.ds.radius.sm}px`,
                        borderBottomRightRadius: `${t.ds.radius.sm}px`,
                      }),
                    })}
                  >
                    <ButtonBase
                      data-day={iso}
                      tabIndex={iso === tabbableDay ? 0 : -1}
                      disabled={disabled}
                      onClick={() => pick(iso)}
                      onMouseEnter={() => setHover(iso)}
                      onFocus={() => setFocusDay(iso)}
                      aria-label={day.format('dddd, D MMMM YYYY')}
                      aria-current={iso === todayIso ? 'date' : undefined}
                      sx={(t) => ({
                        ...t.typography.body2,
                        width: '100%',
                        height: '100%',
                        borderRadius: `${t.ds.radius.sm}px`,
                        fontVariantNumeric: 'tabular-nums',
                        color: inside ? t.tokens.text.accent : t.tokens.text.primary,
                        transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                        '&:hover': { bgcolor: t.palette.action.hover },
                        '&.Mui-focusVisible': { outline: `2px solid ${t.tokens.border.focus}`, outlineOffset: -2 },
                        '&.Mui-disabled': { color: t.tokens.text.disabled },
                        ...(iso === todayIso && !endpoint && {
                          boxShadow: `inset 0 0 0 1px ${t.tokens.border.strong}`,
                        }),
                        ...(endpoint && {
                          bgcolor: t.tokens.brand.primary,
                          color: t.tokens.brand.onPrimary,
                          fontWeight: 600,
                          '&:hover': { bgcolor: t.tokens.brand.primaryHover },
                        }),
                      })}
                    >
                      {day.date()}
                    </ButtonBase>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}
